/**
 * Single canonical, total workflow status -> label map shared by all dashboards.
 *
 * This is the ONE source of truth for translating a complaint's WORKFLOW status
 * (received / investigating / resolved / closed / pending_approval) into its
 * pt-BR label. Unknown / null / legacy values resolve to a NEUTRAL fallback
 * ("Status desconhecido") — NEVER to "Recebida"/"Nova" via default-to-first.
 *
 * Keep the CONCLUSION dimension (status_conclusao / conclusao) separate — this
 * map only covers the workflow status.
 *
 * Dual module shape: authored as ESM `export const` for the CRA/webpack build,
 * with a trailing CommonJS `module.exports` guard so Node's test runner (which
 * uses `require`) can validate the REAL logic in backend/test/*.test.js.
 */

const UNKNOWN_LABEL = 'Status desconhecido';

export const WORKFLOW_STATUS_LABELS = {
  pending_approval: 'Aguardando aprovação',
  received: 'Recebida',
  investigating: 'Em Investigação',
  resolved: 'Resolvida',
  closed: 'Arquivada'
};

const STATUS_BADGE_CLASSES = {
  pending_approval: 'bg-slate-100 text-slate-700',
  received: 'bg-yellow-100 text-yellow-700',
  investigating: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700'
};

const UNKNOWN_BADGE_CLASSES = 'bg-gray-100 text-gray-700';

/**
 * Map a workflow status to its pt-BR label.
 * Returns the neutral fallback for null/undefined/unknown statuses.
 */
export const getStatusLabel = (status) => {
  if (status == null) return UNKNOWN_LABEL;
  return WORKFLOW_STATUS_LABELS[status] || UNKNOWN_LABEL;
};

/**
 * Tailwind badge classes for a workflow status (neutral gray for unknown/null).
 */
export const getStatusBadgeClasses = (status) => {
  if (status == null) return UNKNOWN_BADGE_CLASSES;
  return STATUS_BADGE_CLASSES[status] || UNKNOWN_BADGE_CLASSES;
};

// CommonJS fallback so Node's test runner (require) can validate the real logic.
// CRA/webpack tolerates this trailing guard; the ESM exports above drive the build.
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { WORKFLOW_STATUS_LABELS, getStatusLabel, getStatusBadgeClasses };
}
